import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleDot, Grid3X3, FileUp, ChevronRight, Wrench, Cog, ArrowRightLeft, Calculator, Code, ClipboardList, Target, Shield } from 'lucide-react';

const HomePage = () => {
  const navigate = useNavigate();

  const machineCards = [
    {
      id: 'turning',
      title: 'CNC Turning',
      description: 'Samsung SL25/500 & Hyundai 2600Y turn-mill programs with G71/G72/G73 cycles, threading, live tooling.',
      icon: CircleDot,
      features: ['G71/G72/G73 Cycles', 'Long-Hand Threading', 'Live Tooling', '3D Simulation'],
      controllers: ['FANUC 0i-TD', 'FANUC 0i-TF'],
      color: 'from-emerald-500 to-green-600',
      path: '/turning'
    },
    {
      id: 'milling',
      title: 'CNC Milling',
      description: 'Hyundai 5600HD vertical mill programs for 3-axis, 4th axis, and 5-axis machining.',
      icon: Grid3X3,
      features: ['3-Axis VMC', '4th Axis', '5-Axis', '3D Toolpath'],
      controllers: ['FANUC 0i-MF', 'Siemens', 'Haas'],
      color: 'from-violet-500 to-purple-600',
      path: '/milling'
    },
    {
      id: 'cad-import',
      title: 'CAD Import & AI',
      description: 'Upload blueprints (PDF/JPG), DXF, STL, or STEP files for automatic G-code generation.',
      icon: FileUp,
      features: ['AI Blueprint', 'DXF/STL/STEP', 'Auto Tooling', 'Validation'],
      controllers: ['Turning', 'Milling', 'Turn/Mill'],
      color: 'from-blue-500 to-cyan-600',
      path: '/cad-import'
    },
    {
      id: 'converter',
      title: 'File Converter',
      description: 'Convert OBJ, IGES, DWG, SVG, TIFF, HEIC and more into app-compatible formats.',
      icon: ArrowRightLeft,
      features: ['OBJ/PLY to STL', 'IGES to STEP', 'DWG to DXF', 'Image Convert'],
      controllers: ['Any Source', 'Any Target'],
      color: 'from-teal-500 to-emerald-600',
      path: '/converter'
    },
    {
      id: 'feeds-speeds',
      title: 'Feeds & Speeds',
      description: 'Calculate RPM, SFM, feed rates, DOC, and surface finish for any material and operation.',
      icon: Calculator,
      features: ['RPM/SFM Calc', 'Surface Finish', 'DOC Guide', 'G-Code Snippet'],
      controllers: ['All Materials', 'All Operations'],
      color: 'from-cyan-500 to-blue-600',
      path: '/feeds-speeds'
    },
    {
      id: 'cycle-generator',
      title: 'Cycle Generator',
      description: 'Generate FANUC canned cycle G-code: G71/G70 rough/finish, G72 facing, G76 threading, G81-G84 drill/tap.',
      icon: Code,
      features: ['G71/G70', 'G72 Face', 'G76 Thread', 'G81-G84 Drill'],
      controllers: ['FANUC', 'Inch Mode'],
      color: 'from-orange-500 to-red-600',
      path: '/cycle-generator'
    },
    {
      id: 'job-plans',
      title: 'Job Plans',
      description: 'Link saved programs into sequenced job plans with operation ordering and time estimates.',
      icon: ClipboardList,
      features: ['Op Sequencing', 'Time Estimates', 'G-Code Preview', 'Status Tracking'],
      controllers: ['All Machines'],
      color: 'from-rose-500 to-pink-600',
      path: '/job-plans'
    },
    {
      id: 'v-pulley',
      title: 'V-Pulley Groove',
      description: 'Parametric V-pulley groove generator with peck roughing, finish passes, part-off, and 2D profile visualization.',
      icon: Target,
      features: ['Multi-Groove', 'Peck Rough', 'V-Angle Profile', 'Part-Off'],
      controllers: ['FANUC', 'Inch Mode'],
      color: 'from-violet-500 to-purple-600',
      path: '/v-pulley'
    },
    {
      id: 'soft-jaws',
      title: 'Soft Jaw Generator',
      description: 'Generate G-code for boring soft jaws: OD/ID grip, auto corner relief, grip ratio, thin-wall protection.',
      icon: Shield,
      features: ['OD/ID Grip', 'Auto Relief', 'G71/G70', 'STEP Import'],
      controllers: ['FANUC', 'Inch Mode'],
      color: 'from-teal-500 to-cyan-600',
      path: '/soft-jaws'
    }
  ];

  return (
    <div className="p-4 animate-in">
      {/* Hero Section */}
      <div className="text-center mb-8">
        <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-industrial-safety to-orange-600 rounded-2xl flex items-center justify-center shadow-lg shadow-industrial-safety/30">
          <Cog className="w-10 h-10 text-black animate-spin-slow" style={{ animation: 'spin 20s linear infinite' }} />
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-industrial-text mb-2">
          CNC G-CODE GENERATOR
        </h1>
        <p className="text-industrial-text-secondary max-w-sm mx-auto">
          Professional CNC programs for turning and milling machines
        </p>
      </div>

      {/* Machine Selection Cards */}
      <div className="space-y-4 max-w-lg mx-auto">
        {machineCards.map((machine) => (
          <button
            key={machine.id}
            onClick={() => navigate(machine.path)}
            data-testid={`machine-card-${machine.id}`}
            className="w-full bg-industrial-surface border border-industrial-border rounded-xl p-5 text-left transition-all hover:border-industrial-safety active:scale-[0.98] group"
          >
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${machine.color} flex items-center justify-center flex-shrink-0`}>
                <machine.icon className="w-7 h-7 text-white" />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="font-heading font-bold text-lg text-industrial-text">
                    {machine.title}
                  </h2>
                  <ChevronRight className="w-5 h-5 text-industrial-text-secondary group-hover:text-industrial-safety transition-colors" />
                </div>
                
                <p className="text-sm text-industrial-text-secondary mb-3 line-clamp-2">
                  {machine.description}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {machine.features.map((feature) => (
                    <span 
                      key={feature}
                      className="px-2 py-0.5 bg-industrial-surface-highlight text-xs rounded-full text-industrial-text-secondary"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Controllers */}
                <div className="text-xs text-industrial-text-secondary">
                  <span className="text-industrial-readout font-medium">Controllers: </span>
                  {machine.controllers.join(' • ')}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Quick Stats */}
      <div className="mt-8 grid grid-cols-3 gap-3 max-w-lg mx-auto">
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4 text-center">
          <div className="text-2xl font-heading font-bold text-industrial-safety">4</div>
          <div className="text-xs text-industrial-text-secondary">Controllers</div>
        </div>
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4 text-center">
          <div className="text-2xl font-heading font-bold text-industrial-readout">20+</div>
          <div className="text-xs text-industrial-text-secondary">Operations</div>
        </div>
        <div className="bg-industrial-surface border border-industrial-border rounded-lg p-4 text-center">
          <div className="text-2xl font-heading font-bold text-industrial-success">2D</div>
          <div className="text-xs text-industrial-text-secondary">Simulation</div>
        </div>
      </div>

      {/* Footer Note */}
      <div className="mt-8 text-center">
        <p className="text-xs text-industrial-text-secondary">
          Creator/Developer - Thomas Riddick, CNC Machinist
        </p>
        <button 
          onClick={() => navigate('/settings')}
          className="inline-flex items-center gap-1 mt-2 text-xs text-industrial-readout hover:text-industrial-safety transition-colors"
        >
          <Wrench className="w-3 h-3" />
          Tooling Reference Guides
        </button>
      </div>
    </div>
  );
};

export default HomePage;
